/**
 * knockout-bracket.ts - Knockout Tournament Bracket Rendering
 *
 * Handles the visualization of knockout tournament brackets
 * showing matchups and winners for each round.
 */

import type { Tournament, Match } from '../../../types/api';
import { SPECIAL_PLAYER_IDS, RESULT_TYPES, CSS_CLASSES } from '../../utils/constants';
import { t } from '../../utils/i18n-helper';
import { escapeHtml } from '../../utils/security';
import { getKnockoutRoundName } from '../matches/round-selector';

/**
 * Render knockout bracket
 * @param tournament - Tournament object
 * @param tournamentId - The tournament ID
 * @returns HTML string for the bracket
 */
export function renderKnockoutBracket(tournament: Tournament, tournamentId: string): string {
  const matches = tournament.matches || [];

  if (matches.length === 0) {
    return `<p>${t('pairings.generateToBegin', 'No matches yet. Generate pairings to begin.')}</p>`;
  }

  // Group matches by round
  const matchesByRound: Record<number, Match[]> = {};
  matches.forEach(match => {
    if (!matchesByRound[match.round]) {
      matchesByRound[match.round] = [];
    }
    matchesByRound[match.round]!.push(match);
  });

  const rounds = Object.keys(matchesByRound).map(r => parseInt(r, 10)).sort((a, b) => a - b);
  const playerCount = Array.isArray(tournament.players) ? tournament.players.length : 0;
  const observedPlayerIds = new Set<string>();
  matches.forEach(match => {
    const p1Id = match.player1?.id;
    const p2Id = match.player2?.id;
    if (p1Id && p1Id !== SPECIAL_PLAYER_IDS.BYE) observedPlayerIds.add(p1Id);
    if (p2Id && p2Id !== SPECIAL_PLAYER_IDS.BYE) observedPlayerIds.add(p2Id);
  });
  const inferredPlayerCount = observedPlayerIds.size;

  // Estimate total rounds based on participant count (fallback to observed rounds)
  let totalRounds = rounds.length;
  if (playerCount > 1) {
    totalRounds = Math.max(totalRounds, Math.ceil(Math.log2(playerCount)));
  }
  if (inferredPlayerCount > 1) {
    totalRounds = Math.max(totalRounds, Math.ceil(Math.log2(inferredPlayerCount)));
  }

  let html = `
    <div class="knockout-bracket-container">
      <div class="bracket-header">
        <button class="btn-icon" onclick="printStandings('${tournamentId}')" title="${t('standings.printStandings', 'Print bracket')}">🖨</button>
      </div>
      <div class="knockout-bracket">
  `;

  // Render rounds from left to right
  rounds.forEach(round => {
    const roundName = getKnockoutRoundName(round, totalRounds);
    const roundMatches = matchesByRound[round]!;

    html += `<div class="bracket-round">`;
    html += `<h4 class="bracket-round-title">${roundName}</h4>`;
    html += `<div class="bracket-matches">`;

    roundMatches.forEach(match => {
      const player1 = match.player1 || {};
      const player2 = match.player2 || {};
      const player2Id = player2.id || null;

      const player1Name = `${player1.firstName || ''} ${player1.lastName || ''}`.trim() || SPECIAL_PLAYER_IDS.TBD;
      const player2Name = match.isBye || player2Id === SPECIAL_PLAYER_IDS.BYE
        ? t('pairings.bye', 'BYE')
        : `${player2.firstName || ''} ${player2.lastName || ''}`.trim() || SPECIAL_PLAYER_IDS.TBD;

      const player1Won = match.result && (match.result === RESULT_TYPES.WHITE_WIN || match.result === RESULT_TYPES.BLACK_FORFEIT);
      const player2Won = match.result && (match.result === RESULT_TYPES.BLACK_WIN || match.result === RESULT_TYPES.WHITE_FORFEIT);
      const hasResult = !!(match.result && match.result !== '' && match.result !== 'null');

      html += `<div class="bracket-match">`;
      html += `<div class="bracket-player ${player1Won ? CSS_CLASSES.WINNER : ''} ${hasResult && !player1Won ? CSS_CLASSES.LOSER : ''}">`;
      html += `<span class="player-name">${escapeHtml(player1Name)}</span>`;
      if (player1.rating) html += `<span class="player-rating">${player1.rating}</span>`;
      html += `</div>`;
      html += `<div class="bracket-player ${player2Won ? CSS_CLASSES.WINNER : ''} ${hasResult && !player2Won && player2Id && player2Id !== SPECIAL_PLAYER_IDS.BYE ? CSS_CLASSES.LOSER : ''}">`;
      html += `<span class="player-name">${escapeHtml(player2Name)}</span>`;
      if (player2.rating && player2Id && player2Id !== SPECIAL_PLAYER_IDS.BYE) html += `<span class="player-rating">${player2.rating}</span>`;
      html += `</div>`;
      html += `</div>`;
    });

    html += `</div>`;
    html += `</div>`;
  });

  html += `
      </div>
    </div>
  `;

  return html;
}

// Export to window for global access

window.renderKnockoutBracket = renderKnockoutBracket;
